/**
 * Crow — perches on a high platform, won't move until fed (Babylon.js port).
 *
 * When food is delivered to its nest, the crow drops a key item
 * (gear piece) and flies away (upward animation + fade).
 *
 * Visual: small dark sphere with gentle idle bob.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
import { NPC, NPCConfig } from "./NPC";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";

export class Crow extends NPC {
  override get species(): string { return "crow"; }

  private hasBeenFed = false;
  private droppedItem = false;
  private baseY: number;

  constructor(scene: Scene, key: string, position: Vector3) {
    const config: NPCConfig = {
      key,
      position,
      height: 0.5,
      radius: 0.2,
      color: '#222222',
      behavior: {
        initialState: 'idle',
      },
      modelPath: "/assets/models/npcs/crow.glb",
      modelScale: 3.0,
    };

    super(scene, config);
    this.baseY = position.y;

    // Dark feathered appearance
    this.material.diffuseColor = new Color3(0.12, 0.12, 0.14);
    this.material.emissiveColor = new Color3(0.03, 0.03, 0.04);
  }

  /** Feed the crow. Returns true if this is the first feeding. */
  feed(): boolean {
    if (this.hasBeenFed) return false;
    this.hasBeenFed = true;

    // Happy reaction — brief hop animation then drop item
    const hopAnim = new Animation(
      `crow_${this.npcKey}_hop`,
      "position.y",
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    hopAnim.setKeys([
      { frame: 0, value: this.mesh.position.y },
      { frame: 5, value: this.mesh.position.y + 1.0 },
      { frame: 10, value: this.mesh.position.y },
    ]);

    this.mesh.animations = [hopAnim];
    this.scene.beginAnimation(this.mesh, 0, 10, false, 1.0, () => {
      this.dropItem();
    });

    return true;
  }

  private dropItem(): void {
    if (this.droppedItem) return;
    this.droppedItem = true;

    // Emit event for scene to create the dropped gear
    gameEvents.emit(GameEvents.CROW_DROPPED_ITEM, {
      npcKey: this.npcKey,
      x: this.mesh.position.x,
      y: this.mesh.position.y - 0.5,
    });

    // Fly away — move upward and fade out
    const flyAnim = new Animation(
      `crow_${this.npcKey}_fly`,
      "position.y",
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    flyAnim.setKeys([
      { frame: 0, value: this.mesh.position.y },
      { frame: 45, value: this.mesh.position.y + 15 },
    ]);

    const fadeAnim = new Animation(
      `crow_${this.npcKey}_fade`,
      "visibility",
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    fadeAnim.setKeys([
      { frame: 0, value: 1 },
      { frame: 45, value: 0 },
    ]);

    this.mesh.animations = [flyAnim, fadeAnim];
    this.scene.beginAnimation(this.mesh, 0, 45, false, 1.0, () => {
      this.mesh.isVisible = false;
    });
  }

  get isFed(): boolean {
    return this.hasBeenFed;
  }

  override update(time: number, _deltaSeconds: number): void {
    // Gentle bob while idle (not yet fed)
    if (!this.hasBeenFed) {
      this.mesh.position.y = this.baseY + Math.sin(time * 2) * 0.03;
    }
  }
}
